/**
 * TrialAlertTemplateModal — sysadmin editor for the 4 system trial-alert
 * email templates × 6 locales = 24 rows. GOV-195 / F-SUB-015.
 *
 * Opens from the Platform Defaults page's "Trial & activation" section.
 * Type selector toggles between {pre-expiry, post-expiry} × {company, personal};
 * locale dropdown switches the row for the selected type. Edit tab edits
 * subject + html_content; Preview tab renders the saved-or-unsaved content
 * server-side with sample variables.
 *
 * Sign convention (matches the operator's mental model): negative offset
 * means before expiry → pre-expiry template; >= 0 means at-or-post-expiry
 * → post-expiry template. The TYPE selector here lets sysadmin pick which
 * of those four templates to author; the runtime sweep picks at fire time
 * based on each sub's days_offset and entity_type.
 *
 * Unsaved-changes guard: switching (type, locale) while there are unsaved
 * edits prompts before discarding. The "Save" button is only enabled when
 * dirty.
 */

import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import Modal from '../Modal';
import apiService from '../../services/api';

interface TrialAlertTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TemplateType =
  | 'trial_expiry_alert_company'
  | 'trial_expiry_alert_personal'
  | 'trial_expired_company'
  | 'trial_expired_personal';

const TYPE_OPTIONS: { value: TemplateType; label: string }[] = [
  { value: 'trial_expiry_alert_company', label: 'Pre-expiry · Company' },
  { value: 'trial_expiry_alert_personal', label: 'Pre-expiry · Personal' },
  { value: 'trial_expired_company', label: 'Post-expiry · Company' },
  { value: 'trial_expired_personal', label: 'Post-expiry · Personal' },
];

const LOCALE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'tr', label: 'Türkçe' },
  { value: 'de', label: 'Deutsch' },
  { value: 'es', label: 'Español' },
  { value: 'fi', label: 'Suomi' },
  { value: 'fr', label: 'Français' },
];

// Which variables are valid per template type. Drives the side-panel
// chips so the sysadmin knows what they can substitute. Personal
// templates don't get {{company_name}} or {{users_affected_count}};
// post-expiry uses {{days_since_expiry}} instead of {{days_remaining}}.
//
// `days_label` was removed in migration 156 — it was a Go-computed
// English humanisation ("3 days left") that couldn't be localised.
// Sysadmin now writes the surrounding wording per-locale around the
// raw day counts {{days_remaining}} / {{days_since_expiry}}.
function varsForType(type: TemplateType): string[] {
  const isCompany = type.endsWith('_company');
  const isPre = type.startsWith('trial_expiry_alert_');
  const common = ['recipient_name', 'recipient_email', 'product_name', 'trial_ends_at'];
  if (isPre) {
    common.unshift('days_remaining');
  } else {
    // Post-expiry: raw int + EN-only smart label ("today" / "yesterday" /
    // "N days ago"). Non-EN templates should use the raw {{days_since_expiry}}
    // and write their own phrasing — {{days_since_expiry_label}} is English.
    common.unshift('days_since_expiry', 'days_since_expiry_label');
  }
  if (isCompany) {
    common.push('company_name', 'users_affected_count');
  }
  return common;
}

// Sample-vars used by the Preview tab. Operator-friendly stand-ins so
// the rendered HTML looks realistic. company_name + users_affected_count
// only contribute when the selected type is company-class.
function sampleVarsForType(type: TemplateType): Record<string, unknown> {
  const isCompany = type.endsWith('_company');
  const isPre = type.startsWith('trial_expiry_alert_');
  const base: Record<string, unknown> = {
    recipient_name: 'Tolgay',
    recipient_email: 'tolgay@stateful.art',
    product_name: 'Governate',
    trial_ends_at: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
  };
  if (isPre) {
    base.days_remaining = 3;
  } else {
    base.days_since_expiry = 1;
    base.days_since_expiry_label = 'yesterday'; // matches what Go computes for offset=1
  }
  if (isCompany) {
    base.company_name = 'Acme Inc';
    base.users_affected_count = 12;
  }
  return base;
}

const TrialAlertTemplateModal: React.FC<TrialAlertTemplateModalProps> = ({ isOpen, onClose }) => {
  const [selectedType, setSelectedType] = useState<TemplateType>('trial_expiry_alert_company');
  const [selectedLocale, setSelectedLocale] = useState('en');
  const [activeTab, setActiveTab] = useState<'edit' | 'preview'>('edit');

  // Currently-loaded row's saved subject + body. Compared against the
  // editor state to compute the dirty flag.
  const [loadedSubject, setLoadedSubject] = useState('');
  const [loadedHTML, setLoadedHTML] = useState('');
  const [draftSubject, setDraftSubject] = useState('');
  const [draftHTML, setDraftHTML] = useState('');

  const [previewSubject, setPreviewSubject] = useState('');
  const [previewHTML, setPreviewHTML] = useState('');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const isDirty = draftSubject !== loadedSubject || draftHTML !== loadedHTML;

  // Load the (type, locale) row on selection change.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    apiService
      .getSystemEmailTemplates({ type: selectedType, locale: selectedLocale })
      .then((data) => {
        if (cancelled) return;
        const row = Array.isArray(data) ? data[0] : data;
        if (!row) {
          toast.error(`Template not found for ${selectedType} / ${selectedLocale}`);
          return;
        }
        setLoadedSubject(row.subject);
        setLoadedHTML(row.html_content);
        setDraftSubject(row.subject);
        setDraftHTML(row.html_content);
      })
      .catch((err) => {
        if (!cancelled) toast.error(`Failed to load template: ${err?.response?.data?.error || err.message}`);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, selectedType, selectedLocale]);

  // Preview tab — server-render whenever it activates OR drafts change while open.
  useEffect(() => {
    if (!isOpen || activeTab !== 'preview') return;
    let cancelled = false;
    setPreviewing(true);
    // Save first so the preview reflects the in-flight edits; the preview
    // endpoint reads from the DB row, so unsaved drafts won't show through.
    // (Trade-off: previewing unsaved edits would need a different endpoint
    // shape. For now we render the saved version and document this.)
    apiService
      .previewSystemEmailTemplate({
        type: selectedType,
        locale: selectedLocale,
        sample_vars: sampleVarsForType(selectedType),
      })
      .then((data) => {
        if (cancelled) return;
        setPreviewSubject(data.subject);
        setPreviewHTML(data.html_content);
      })
      .catch((err) => {
        if (!cancelled) toast.error(`Preview failed: ${err?.response?.data?.error || err.message}`);
      })
      .finally(() => {
        if (!cancelled) setPreviewing(false);
      });
  }, [isOpen, activeTab, selectedType, selectedLocale]);

  // Switch (type, locale) — confirm if dirty.
  const tryChangeType = (next: TemplateType) => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard?')) return;
    setSelectedType(next);
  };
  const tryChangeLocale = (next: string) => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard?')) return;
    setSelectedLocale(next);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await apiService.updateSystemEmailTemplate(selectedType, selectedLocale, {
        subject: draftSubject,
        html_content: draftHTML,
      });
      setLoadedSubject(updated.subject);
      setLoadedHTML(updated.html_content);
      toast.success(`Saved ${selectedType} / ${selectedLocale}`);
    } catch (err: any) {
      toast.error(`Save failed: ${err?.response?.data?.error || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard and close?')) return;
    onClose();
  };

  const availableVars = useMemo(() => varsForType(selectedType), [selectedType]);

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div
        role="dialog"
        aria-label="Edit trial alert email"
        className="w-[min(90vw,960px)] max-h-[90vh] overflow-hidden bg-white dark:bg-gray-900 rounded-lg shadow-xl flex flex-col"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Edit trial alert email
          </h2>
          <button
            onClick={handleClose}
            aria-label="Close"
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            ✕
          </button>
        </div>

        {/* Type + locale selectors */}
        <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 flex gap-3 items-center flex-wrap">
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Template</label>
            <select
              data-testid="template-type-selector"
              value={selectedType}
              onChange={(e) => tryChangeType(e.target.value as TemplateType)}
              className="text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2"
            >
              {TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Locale</label>
            <select
              data-testid="template-locale-selector"
              value={selectedLocale}
              onChange={(e) => tryChangeLocale(e.target.value)}
              className="text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2"
            >
              {LOCALE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          {isDirty && (
            <span className="text-xs text-amber-600 dark:text-amber-400 ml-auto">● unsaved changes</span>
          )}
        </div>

        {/* Tabs */}
        <div className="px-6 pt-3 border-b border-gray-200 dark:border-gray-700 flex gap-2">
          <button
            role="tab"
            aria-selected={activeTab === 'edit'}
            onClick={() => setActiveTab('edit')}
            className={`px-3 py-2 text-sm rounded-t-md ${
              activeTab === 'edit'
                ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 border-b-transparent'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            Edit
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'preview'}
            onClick={() => setActiveTab('preview')}
            className={`px-3 py-2 text-sm rounded-t-md ${
              activeTab === 'preview'
                ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 border-b-transparent'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            Preview
          </button>
        </div>

        {/* Body — Edit or Preview */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="text-sm text-gray-500 dark:text-gray-400">Loading...</div>
          ) : activeTab === 'edit' ? (
            <div className="grid grid-cols-1 md:grid-cols-[1fr_220px] gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Subject
                </label>
                <input
                  type="text"
                  name="subject"
                  value={draftSubject}
                  onChange={(e) => setDraftSubject(e.target.value)}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 text-sm"
                />
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mt-4 mb-1">
                  HTML body
                </label>
                <textarea
                  name="html_content"
                  value={draftHTML}
                  onChange={(e) => setDraftHTML(e.target.value)}
                  rows={18}
                  className="w-full font-mono text-xs rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Available variables
                </label>
                <ul className="space-y-1">
                  {availableVars.map((v) => (
                    <li key={v}>
                      <code className="text-xs text-blue-600 dark:text-blue-400">{`{{${v}}}`}</code>
                    </li>
                  ))}
                </ul>
                <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                  Unprovided variables render as empty strings at send time.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {previewing ? (
                <div className="text-sm text-gray-500 dark:text-gray-400">Rendering...</div>
              ) : (
                <>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Subject:</div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">
                    {previewSubject}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-3">Body:</div>
                  <div
                    className="border border-gray-200 dark:border-gray-700 rounded-md p-4 bg-gray-50 dark:bg-gray-800 max-h-[400px] overflow-y-auto"
                    dangerouslySetInnerHTML={{ __html: previewHTML }}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Preview reflects the saved row (server-rendered). Save first to preview your latest edits.
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button
            onClick={handleClose}
            className="px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            Close
          </button>
          <button
            onClick={handleSave}
            disabled={!isDirty || saving}
            className="px-3 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default TrialAlertTemplateModal;
